import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TooltipCustom } from "@services/ui-components";
import useCopyToClipboard from "@me-team/common/src/hooks/useCopyToClipboard";

type PlacementType = "top" | "right" | "bottom" | "left" | "auto";

interface CopyOriginButtonProps {
    originText: string | null | undefined;
    show: boolean;
    onCopied: () => void;
    placement?: PlacementType;
    className?: string;
    ariaLabel?: string;
}

const CopyOriginButton: React.FC<CopyOriginButtonProps> = ({
    originText,
    show,
    onCopied,
    placement = "top",
    className,
    ariaLabel,
}) => {
    const { t } = useTranslation();
    const [, copy] = useCopyToClipboard();
    const [isCopied, setIsCopied] = useState(false);
    const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
        () => () => {
            if (copyTimerRef.current) {
                clearTimeout(copyTimerRef.current);
            }
        },
        []
    );

    const trimmedOriginText = originText?.trim() ?? "";

    if (!show || !trimmedOriginText) {
        return null;
    }

    const handleClick = async () => {
        const copied = await copy(trimmedOriginText);
        if (!copied) {
            return;
        }
        onCopied();
        setIsCopied(true);
        if (copyTimerRef.current) {
            clearTimeout(copyTimerRef.current);
        }
        copyTimerRef.current = setTimeout(() => {
            setIsCopied(false);
            copyTimerRef.current = null;
        }, 1000);
    };

    return (
        <TooltipCustom placement={placement} text={t("Copy original")}>
            <button
                type="button"
                aria-label={ariaLabel ?? t("Copy original")}
                className={`btn btn-link p-0 border-0 text-primary ${className ?? ""}`}
                onMouseDown={(event) => event.preventDefault()}
                onClick={handleClick}
            >
                {isCopied ? <i className="bi bi-check-lg text-primary" /> : <i className="bi bi-copy" />}
            </button>
        </TooltipCustom>
    );
};

export default CopyOriginButton;
